import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, photoUrl } from "../lib/api";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RoleAvatar } from "../components/RoleAvatar";
import { PROVINCES } from "../lib/constants";
import { BadgeCheck, Search, Store, MapPin, PackageOpen, Globe, List, Map as MapIcon } from "lucide-react";
import { BUSINESS_TYPE_LABELS } from "../components/profile/SpecialtyChips";
import { DirectoryMap } from "../components/DirectoryMap";
import { OpenBadge } from "../components/profile/StoreMap";

const provName = (code) => (PROVINCES.find((p) => p.code === code)?.name) || code;
const KIND = {
  business: { api: "/businesses", badge: "Verified Business", title: "Verified Business Directory", noun: ["business", "businesses"],
    sub: "Browse trusted shops, breeders and shelters whose identity we've verified. Buy with confidence from businesses with a track record.",
    pill: "bg-amber-100 text-amber-700", dot: "bg-amber-500", hover: "hover:border-amber-300" },
  breeder: { api: "/breeders", badge: "Verified Breeder", title: "Verified Breeder Directory", noun: ["breeder", "breeders"],
    sub: "Meet breeders whose identity and practices we've verified. Find the right breeder for your next family member.",
    pill: "bg-purple-100 text-purple-700", dot: "bg-purple-600", hover: "hover:border-purple-300" },
};
const SELLS = { online: "Online Sale", storefront: "Store Front" };

const ChipFilter = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {Object.entries(options).map(([k, label]) => {
      const on = value.includes(k);
      return (
        <button key={k} type="button" onClick={() => onChange(on ? value.filter((x) => x !== k) : [...value, k])} data-testid={`${testid}-${k}`}
          className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-[#12234A] border-[#12234A] text-white" : "bg-white border-slate-200 text-slate-600 hover:border-[#12234A]"}`}>{label}</button>
      );
    })}
  </div>
);

export default function Businesses({ kind = "business" }) {
  const K = KIND[kind];
  const [data, setData] = useState(null); // {businesses, available_provinces, available_types}
  const [q, setQ] = useState("");
  const [province, setProvince] = useState("all");
  const [type, setType] = useState("all");
  const [animals, setAnimals] = useState([]);
  const [sells, setSells] = useState([]);
  const [view, setView] = useState("list");

  const load = useCallback(() => {
    setData(null);
    api.get(K.api).then((r) => setData(r.data)).catch(() => setData({ businesses: [], available_provinces: [], available_types: [] }));
  }, [K.api]);
  useEffect(() => { load(); }, [load]);

  const all = data?.businesses || null;
  const needle = q.trim().toLowerCase();
  const list = (all || []).filter((b) =>
    (!needle || (b.name || "").toLowerCase().includes(needle) || (b.bio || "").toLowerCase().includes(needle)) &&
    (type === "all" || b.seller_type === type) &&
    (province === "all" || (b.provinces || []).includes(province)) &&
    (!animals.length || animals.some((a) => (b.animal_types || []).includes(a))) &&
    (!sells.length || sells.every((s) => (b.sells || []).includes(s))));
  const filtered = !!(province !== "all" || type !== "all" || q || animals.length || sells.length);
  const clear = () => { setProvince("all"); setType("all"); setQ(""); setAnimals([]); setSells([]); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="businesses-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <span className={`inline-flex items-center gap-1.5 ${K.pill} text-xs font-bold px-2.5 py-1 rounded-full`}>
            <BadgeCheck size={14} /> {K.badge}
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#12234A] mt-3" data-testid="directory-title">{K.title}</h1>
          <p className="text-slate-500 mt-2 max-w-xl">{K.sub}</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="businesses-search" placeholder="Search by name or keyword…" className="pl-9 h-11 rounded-xl" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-4" data-testid="businesses-chip-filters">
        <div className="flex items-center gap-2 flex-wrap"><span className="text-xs font-bold uppercase tracking-wide text-slate-400">Animals</span>
          <ChipFilter options={BUSINESS_TYPE_LABELS} value={animals} onChange={setAnimals} testid="dir-animal" /></div>
        <div className="flex items-center gap-2 flex-wrap"><span className="text-xs font-bold uppercase tracking-wide text-slate-400">How they sell</span>
          <ChipFilter options={SELLS} value={sells} onChange={setSells} testid="dir-sells" /></div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="businesses-filters">
        <Select value={province} onValueChange={setProvince}>
          <SelectTrigger data-testid="businesses-filter-province" className="h-10 rounded-xl w-full sm:w-52"><SelectValue placeholder="All provinces" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All provinces</SelectItem>
            {(data?.available_provinces || []).map((c) => (
              <SelectItem key={c} value={c} data-testid={`businesses-province-${c}`}>{provName(c)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="businesses-filter-type" className="h-10 rounded-xl w-full sm:w-48"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All types</SelectItem>
            {(data?.available_types || []).map((t) => (
              <SelectItem key={t} value={t} data-testid={`businesses-type-${t}`}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filtered && (
          <button onClick={clear} data-testid="businesses-clear-filters" className="text-sm text-slate-500 hover:text-[#F5551E] font-medium">Clear filters</button>
        )}
        {all && <span className="text-sm text-slate-400 ml-auto" data-testid="businesses-count">{list.length} {K.noun[list.length === 1 ? 0 : 1]}</span>}
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5" data-testid="directory-view-toggle">
          {[["list", "List", List], ["map", "Map", MapIcon]].map(([v, label, Icon]) => (
            <button key={v} type="button" onClick={() => setView(v)} data-testid={`directory-view-${v}`}
              className={`h-8 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${view === v ? "bg-[#12234A] text-white" : "text-slate-600 hover:text-[#12234A]"}`}><Icon size={14} /> {label}</button>
          ))}
        </div>
      </div>

      {data === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-36 rounded-2xl" />)}
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-2xl border border-slate-200/80" data-testid="businesses-empty">
          <Store size={44} className="mx-auto text-slate-300" />
          <p className="text-slate-500 mt-3">{filtered ? `No verified ${K.noun[1]} match your filters.` : `No verified ${K.noun[1]} yet — check back soon.`}</p>
        </div>
      ) : view === "map" ? (
        <DirectoryMap list={list} noun={K.noun[1]} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((b) => (
            <Link key={b.user_id} to={`/seller/${b.user_id}`} data-testid={`business-card-${b.user_id}`}
              className={`bg-white rounded-2xl border border-slate-200/80 p-5 ${K.hover} hover:shadow-md transition-all group`}>
              <div className="flex items-start gap-3">
                {b.picture ? (
                  <img src={photoUrl(b.picture)} alt={b.name} className="h-14 w-14 rounded-xl object-cover shrink-0" />
                ) : (
                  <RoleAvatar user={{ role: "user", name: b.name }} size={56} />
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-display font-bold text-[#12234A] truncate group-hover:text-[#F5551E] transition-colors flex items-center gap-1.5">
                    {b.name}
                    <span className={`inline-flex items-center justify-center h-4 w-4 rounded-full ${K.dot} shrink-0`} title={K.badge}><BadgeCheck size={11} className="text-white" /></span>
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><MapPin size={11} /> {kind === "breeder" ? "Breeder" : b.seller_type}{b.province ? ` · ${provName(b.province)}` : ""}</p>
                </div>
              </div>
              {b.tagline ? (
                <p className="text-sm text-[#12234A] font-medium mt-3 line-clamp-2 italic" data-testid={`business-tagline-${b.user_id}`}>“{b.tagline}”</p>
              ) : b.bio ? (
                <p className="text-sm text-slate-500 mt-3 line-clamp-2">{b.bio}</p>
              ) : null}
              {(b.sells?.length > 0 || b.animal_types?.length > 0 || b.open_now != null) && (
                <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid={`business-tags-${b.user_id}`}>
                  <OpenBadge open={b.open_now} closure={b.closed_today} testid={`business-open-${b.user_id}`} />
                  {b.animal_types.map((a) => <span key={a} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{BUSINESS_TYPE_LABELS[a]}</span>)}
                  {b.sells.map((s) => <span key={s} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{SELLS[s]}</span>)}
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-1.5 text-sm font-semibold text-[#12234A]">
                <PackageOpen size={15} className="text-[#F5551E]" /> {b.active_listings} active listing{b.active_listings === 1 ? "" : "s"}
                {b.website && (
                  <span
                    role="link"
                    tabIndex={0}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(b.website, "_blank", "noopener,noreferrer"); }}
                    data-testid={`business-website-${b.user_id}`}
                    className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-[#F5551E] hover:underline"
                  >
                    <Globe size={13} /> Website
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
